<script setup lang="ts">
import { ref, watch } from 'vue'

import { setContextPageTitle } from '@collector/shared-plugin-sentry-vue'
import { stopLoader } from '@collector/shared-ui'

import {
  useCompetitionQuery,
  useEvent,
  useEventId,
  useEventIncidentsQuery,
  useEventIncidentsUpdateQuery,
  useEventScoutsQuery,
  useEventSettings,
  useLiveCompetition,
  useLiveEventScouts,
  useLiveIncidents,
  useLiveSubParticipants,
  useParticipantsQuery,
  useSeasonDetailsQuery,
  useSportQuery,
  useSubParticipantsQuery,
  useSwappableIncidentsQuery,
} from './composables'
import { useQueueClient } from './composables/queue/useQueueClient'
import { provideAndInitRelation } from './provide-inject'
import {
  initEventIncidentsQueue,
  setTeamSidesColors,
  useConfirmIncident,
  validateRelationAccess,
} from './utils'
import { getSportRelationComponent } from './utils/sportComponents'

// state
const initialized = ref(false)

// data
const eventId = useEventId()

const competitionQuery = useCompetitionQuery()
const scoutsQuery = useEventScoutsQuery()
const incidentsQuery = useEventIncidentsQuery()
const incidentsUpdateQuery = useEventIncidentsUpdateQuery()
const subParticipantsQuery = useSubParticipantsQuery()
const swappableQuery = useSwappableIncidentsQuery()
const participantsQuery = useParticipantsQuery()
const sportQuery = useSportQuery()
const seasonDetailsQuery = useSeasonDetailsQuery()

await Promise.all([
  competitionQuery.suspense(),
  scoutsQuery.suspense(),
  incidentsQuery.suspense(),
  subParticipantsQuery.suspense(),
  swappableQuery.suspense(),
  participantsQuery.suspense(),
  sportQuery.suspense(),
  seasonDetailsQuery.suspense(),
])

const { eventSettings, loaded: eventSettingsLoaded } = useEventSettings()
await eventSettingsLoaded

const { event } = useEvent()

const { connected, client } = useQueueClient()

// init
const sportRelationComponent = getSportRelationComponent(sportQuery.data.value!)
validateRelationAccess(scoutsQuery.data.value!)

const eventIncidentsQueue = await initEventIncidentsQueue(eventId.value)

const {
  onQueueRabbitConfirmIncident,
  onQueueRabbitErrorMessage,
  acknowledgeIncidentUuids,
} = useConfirmIncident(incidentsUpdateQuery.data)
eventIncidentsQueue.setConfirmQueueIncident(onQueueRabbitConfirmIncident)

watch(
  connected,
  (isConnected, _, onCleanup) => {
    if (!isConnected) return
    onCleanup(
      client.subscribe(`events/${eventId.value}/error`, {
        onMessageBatch: (messages) => {
          for (const message of messages) {
            onQueueRabbitErrorMessage(message)
          }
        },
      }),
    )
  },
  { immediate: true },
)

provideAndInitRelation({
  eventIncidentsQueue,
})

// Live queue channels are session-scoped: they must stay subscribed for as long as this view is
// mounted. Establishing them here binds them to Relation's scope; doing it in consumer components
// would silently drop live updates as soon as the last consumer unmounts.
useLiveCompetition()
useLiveSubParticipants()
useLiveEventScouts()
useLiveIncidents(acknowledgeIncidentUuids)

setTeamSidesColors(!!eventSettings.value.teamColorsSwitched)

/* Sentry grouped issue title */
const [homeParticipant, awayParticipant] = participantsQuery.data.value!
setContextPageTitle(
  `${homeParticipant.short_name} - ${awayParticipant.short_name} (${event.value!.start_date})`,
)

document.title = `${homeParticipant.short_name} - ${awayParticipant.short_name}`

initialized.value = true
stopLoader()
</script>

<template>
  <div>
    <!--  ^ wrapping in 1 root element (<div>) is required to prevent live-reload errors when using <Suspense> -->
    <template v-if="initialized">
      <component :is="sportRelationComponent" />
    </template>

    <div v-else>Error during loading Relation</div>
  </div>
</template>
